import React, { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building2 } from 'lucide-react';
import { acceptInvite } from '../api/tenants';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';

/** The one centred card all three states of this page render into. */
const Shell: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-4 py-8">
    <Card className="w-full max-w-md">
      <Card.Body>{children}</Card.Body>
    </Card>
  </div>
);

/**
 * Accept Invitation Page (multi-tenancy, v0.13.0)
 *
 * Public page reached from the link in an invitation email. It is the ONLY way a client user
 * gets into the product: registration is bootstrap-only, because open self-registration is
 * incompatible with selling seats and would leave a new account with no workspace to work in.
 *
 * Unauthenticated by design — the emailed token is the proof of identity, exactly as for a
 * password reset. Accepting activates the login and every membership it is holding, so
 * someone invited to two workspaces before their first sign-in gets both at once.
 *
 * Deliberately kept separate from PasswordResetPage rather than sharing it. The two flows
 * have the same mechanics but different meaning, and the copy matters here: the person has
 * never seen this product before and needs to know they are joining a workspace, not
 * recovering an account they have lost.
 */
const AcceptInvitePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const token = new URLSearchParams(location.search).get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validate = (): string | null => {
    if (password.length < 8) return 'Password must be at least 8 characters long.';
    if (password !== confirmPassword) return 'The two passwords do not match.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (!token) {
      setError('This invitation link is missing its token.');
      return;
    }

    setLoading(true);
    try {
      await acceptInvite({ token, password });
      setSuccess(true);
      // Give the confirmation a moment to be read before moving on.
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Could not accept this invitation. It may have expired or already been used.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Shell>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Invitation link incomplete
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          This link is missing its invitation token, so we cannot tell which workspace it is
          for. Open the link directly from your invitation email, or ask whoever invited you
          to send a new one.
        </p>
        <Link to="/login">
          <Button variant="outline">Back to sign in</Button>
        </Link>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <h1 className="text-xl font-bold text-green-700 dark:text-green-400 mb-2">
          You&apos;re all set
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Your password has been set and your workspace access is active. Taking you to the
          sign-in page…
        </p>
        <Link to="/login">
          <Button variant="primary">Sign in now</Button>
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Accept your invitation
        </h1>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Choose a password to finish setting up your account and join your workspace.
      </p>

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          helperText="At least 8 characters"
          rightIcon={showPassword ? EyeOff : Eye}
          onRightIconClick={() => setShowPassword((v) => !v)}
          rightIconLabel={showPassword ? 'Hide password' : 'Show password'}
          required
        />

        <Input
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
        />

        <Button type="submit" variant="primary" disabled={loading} className="w-full">
          {loading ? 'Setting your password…' : 'Accept invitation'}
        </Button>
      </form>

      <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
        Already have a password?{' '}
        <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline">
          Sign in instead
        </Link>
        .
      </p>
    </Shell>
  );
};

export default AcceptInvitePage;
